import { FormControlLabel, Grid, useEventCallback } from '@mui/material';
import { Fragment, memo, useEffect, useMemo } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import { FormikErrors, FormikTouched } from 'formik';
import { SectionVM } from 'civil-status/view-models/step-form';
import { ConfigurationType } from 'civil-status/constants/configuration-type';
import ListTemplateTranscription, { ItemTemplateVM } from '../list/list-template-transcription';
import CitizenshipCit06 from './citizenship-cit-form/citizenship-cit06';
import CitizenshipCit08 from './citizenship-cit-form/citizenship-cit08';
import ListAnsc from '../../../../../../shared/list-ansc';
import { ItemVM } from 'shell/view-models/item';
import { Checkbox } from '@mui/material';

interface ActConsentProps {
  disabled?: boolean;
  values?: SectionVM;
  errors?: FormikErrors<SectionVM>;
  touched?: FormikTouched<SectionVM>;
  onChange: (key: keyof SectionVM, value: SectionVM[keyof SectionVM]) => void;
  onChanges: (values: SectionVM) => void;
  configuration?: string;
  tipoEvento?: string;
  idEvento?: number;
  idUseCase?: string;
  controllSentenceRecognition: (sentenceRecognition?: string) => void;
}

const CitizenshipCitComponent = ({
  disabled = false,
  values,
  errors,
  touched,
  onChange,
  onChanges,
  configuration,
  tipoEvento,
  idEvento,
  idUseCase,
  controllSentenceRecognition,
}: ActConsentProps): React.ReactElement => {
  const handleListTemplateTranscriptionChange = useEventCallback((value?: ItemTemplateVM) => {
    onChanges({
      ...(values || {}),
      templateTranscription: value?.id?.toString(),
      templateTranscriptionText: value?.valueTemplate || '',
    });
  });

  const handleListTemplateTranscription2Change = useEventCallback((value?: ItemTemplateVM) => {
    onChanges({
      ...(values || {}),
      templateTranscription2: value?.id?.toString(),
      summaryStatement: value?.valueTemplate || '',
    });
  });

  const handleListPresentationDocument = useEventCallback((item?: ItemVM) => {
    onChange('presentationDocument', item?.id);
  });

  const handleListSentenceRecognition = useEventCallback((item?: ItemVM) => {
    onChange('sentenceRecognition', item?.id);
  });

  const handleListParentCitizenship = useEventCallback((item?: ItemVM) => {
    onChange('parentCitizenship', item?.id);
  });

  useEffect(() => {
    controllSentenceRecognition(values?.sentenceRecognition);
  }, [values?.sentenceRecognition]);

  const isDsCit2 = useMemo(() => {
    return configuration === ConfigurationType.DSCIT2;
  }, [configuration]);

  const isDsCit3 = useMemo(() => {
    return configuration === ConfigurationType.DSCIT3;
  }, [configuration]);

  const isDsCit4 = useMemo(() => {
    return configuration === ConfigurationType.DSCIT4;
  }, [configuration]);

  const isDsCit6 = useMemo(() => {
    return configuration === ConfigurationType.DSCIT6;
  }, [configuration]);

  const isDsCit7 = useMemo(() => {
    return configuration === ConfigurationType.DSCIT7;
  }, [configuration]);

  const isDsCit8 = useMemo(() => {
    return configuration === ConfigurationType.DSCIT8;
  }, [configuration]);

  const isDsCit9 = useMemo(() => {
    return configuration === ConfigurationType.DSCIT9;
  }, [configuration]);

  const isDsCit10 = useMemo(() => {
    return configuration === ConfigurationType.DSCIT10;
  }, [configuration]);

  const isDsCitFr = useMemo(() => {
    return configuration === ConfigurationType.DSCITFR;
  }, [configuration]);

  return (
    <Grid container spacing={2} item>
      {isDsCit3 || isDsCit4 || isDsCit6 ? undefined : (
        <Grid item xs={12}>
          <PmiTextField
            label="Indirizzo posta autorità"
            aria-label="Indirizzo posta autorità"
            id="authorityPostAddress"
            name="authorityPostAddress"
            fullWidth
            disabled={disabled}
            helperText={errors?.authorityPostAddress}
            error={touched?.authorityPostAddress && Boolean(errors?.authorityPostAddress)}
            type="text"
            variant="outlined"
            value={values?.authorityPostAddress || ''}
            onChange={(event) => {
              onChange('authorityPostAddress', event.target.value);
            }}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <ListTemplateTranscription
          value={values?.templateTranscription2 || ''}
          onChange={handleListTemplateTranscription2Change}
          tipoEvento={tipoEvento}
          idEvento={idEvento}
          disabled={disabled}
          idUseCase={idUseCase}
          error={Boolean(errors?.templateTranscription2) && touched?.templateTranscription2}
        />
      </Grid>
      <Grid item xs={12}>
        <PmiTextField
          label="Riassunto della dichiarazione (per annotazione su atto di nascita) *"
          aria-label="Riassunto della dichiarazione (per annotazione su atto di nascita)"
          id="summaryStatement"
          name="summaryStatement"
          fullWidth
          disabled={disabled}
          helperText={errors?.summaryStatement}
          error={touched?.summaryStatement && Boolean(errors?.summaryStatement)}
          type="text"
          multiline
          rows={5}
          variant="outlined"
          value={values?.summaryStatement || ''}
          onChange={(event) => {
            onChange('summaryStatement', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <ListAnsc
          label="Presentazione documenti"
          idDominio="36"
          value={values?.presentationDocument || ''}
          onChange={handleListPresentationDocument}
          disabled={disabled}
          error={Boolean(errors?.presentationDocument) && touched?.presentationDocument}
          required
        />
      </Grid>
      {isDsCit2 ? (
        <Fragment>
          <Grid item xs={12}>
            <ListTemplateTranscription
              value={values?.templateTranscription || ''}
              onChange={handleListTemplateTranscriptionChange}
              tipoEvento={tipoEvento}
              idEvento={idEvento}
              disabled={disabled}
              error={Boolean(errors?.templateTranscription) && touched?.templateTranscription}
            />
          </Grid>
          <Grid item xs={12}>
            <PmiTextField
              label="Dati residenza biennale *"
              aria-label="Dati residenza biennale"
              id="templateTranscriptionText"
              name="templateTranscriptionText"
              fullWidth
              disabled={disabled}
              helperText={errors?.templateTranscriptionText}
              error={
                touched?.templateTranscriptionText && Boolean(errors?.templateTranscriptionText)
              }
              type="text"
              multiline
              rows={5}
              variant="outlined"
              value={values?.templateTranscriptionText || ''}
              onChange={(event) => {
                onChange('templateTranscriptionText', event.target.value);
              }}
            />
          </Grid>
        </Fragment>
      ) : undefined}
      {isDsCit4 || isDsCit6 || isDsCit7 ? (
        <Grid item xs={12}>
          <PmiTextField
            label="Motivazione perdita cittadinanza *"
            aria-label="Motivazione perdita cittadinanza"
            id="reasonLossCitizenship"
            name="reasonLossCitizenship"
            fullWidth
            disabled={disabled}
            helperText={errors?.reasonLossCitizenship}
            error={touched?.reasonLossCitizenship && Boolean(errors?.reasonLossCitizenship)}
            type="text"
            variant="outlined"
            multiline
            minRows={5}
            value={values?.reasonLossCitizenship || ''}
            onChange={(event) => {
              onChange('reasonLossCitizenship', event.target.value);
            }}
          />
        </Grid>
      ) : undefined}
      {isDsCit9 ? (
        <Grid item xs={12} md={6}>
          <ListAnsc
            label="Riconoscimento sentenza"
            idDominio="98"
            value={values?.sentenceRecognition || ''}
            onChange={handleListSentenceRecognition}
            disabled={disabled}
            error={Boolean(errors?.sentenceRecognition) && touched?.sentenceRecognition}
            required
          />
        </Grid>
      ) : undefined}
      {isDsCit10 ? (
        <Grid item xs={12} md={6}>
          <ListAnsc
            label="Cittadinanza genitore"
            idDominio="84"
            value={values?.parentCitizenship || ''}
            onChange={handleListParentCitizenship}
            disabled={disabled}
            error={Boolean(errors?.parentCitizenship) && touched?.parentCitizenship}
            required
            onlyId={['1', '4']}
          />
        </Grid>
      ) : undefined}
      {isDsCitFr ? (
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Checkbox
                id="isResidencePresentationDocuments"
                checked={values?.isResidencePresentationDocuments || false}
                onChange={() => {
                  onChange(
                    'isResidencePresentationDocuments',
                    !values?.isResidencePresentationDocuments
                  );
                }}
                inputProps={{ 'aria-label': 'Residenza provata da altra documentazione' }}
              />
            }
            label="Residenza provata da altra documentazione"
          />
        </Grid>
      ) : undefined}
      {isDsCit6 ? (
        <CitizenshipCit06
          disabled={disabled}
          values={values}
          errors={errors}
          touched={touched}
          onChange={onChange}
        />
      ) : undefined}
      {isDsCit8 ? (
        <CitizenshipCit08
          disabled={disabled}
          values={values}
          errors={errors}
          touched={touched}
          onChange={onChange}
        />
      ) : undefined}
    </Grid>
  );
};

CitizenshipCitComponent.displayName = 'Form Citizenship Cit component';

const CitizenshipCit = memo(CitizenshipCitComponent);

export default CitizenshipCit;
